import { employeeService } from '../../../Services/EmployeeService';

type PendingCall = {
  resolve: (result: boolean) => void;
  reject: (error: unknown) => void;
};

const DEBOUNCE_MS = 400;

/**
 * Checks whether an employee's personnel number is already taken for a given employer/connection
 * set. Formik re-runs every registered field's validator on every keystroke of ANY field, so this
 * coalesces repeated calls for the same (value, connectionIds) pair into a single debounced
 * backend request - piggybacking late callers onto an already-scheduled/in-flight request instead
 * of firing a duplicate one - and caches the last result to skip the network entirely when the
 * value hasn't actually changed.
 */
export const createPersonnelNumberValidationService = () => {
  let lastValidated: { key: string; result: boolean } | null = null;
  const pendingByKey = new Map<string, PendingCall[]>();
  const timerByKey = new Map<string, ReturnType<typeof setTimeout>>();
  const inFlightKeys = new Set<string>();

  const check = async (employerId: string, connectionIds: string[], value: string, key: string) => {
    const calls = pendingByKey.get(key) ?? [];
    pendingByKey.delete(key);

    let result: boolean | undefined;
    let error: unknown;

    try {
      await Promise.all(
        connectionIds.map(connectionId =>
          employeeService.validateField(employerId, 'personnelNumber', value, connectionId),
        ),
      );
      result = true;
    } catch (err: any) {
      if (err?.response?.status === 422) {
        result = false;
      } else {
        error = err;
      }
    }

    inFlightKeys.delete(key);

    const settle = (call: PendingCall) => (result !== undefined ? call.resolve(result) : call.reject(error));
    calls.forEach(settle);

    // Callers that arrived for this exact key while the request above was in flight: settle them
    // from the result we just got instead of triggering another request.
    const lateCalls = pendingByKey.get(key);
    if (lateCalls?.length) {
      pendingByKey.delete(key);
      lateCalls.forEach(settle);
    }

    if (result !== undefined) {
      lastValidated = { key, result };
    }
  };

  const validate = (employerId: string, connectionIds: string[], value: string): Promise<boolean> => {
    if (!value) return Promise.resolve(true);

    const key = `${value}|${connectionIds.join(',')}`;

    if (lastValidated?.key === key) {
      return Promise.resolve(lastValidated.result);
    }

    return new Promise<boolean>((resolve, reject) => {
      const pending = pendingByKey.get(key) ?? [];
      pending.push({ resolve, reject });
      pendingByKey.set(key, pending);

      // A request for this exact value is already scheduled or in flight - piggyback on it.
      if (timerByKey.has(key) || inFlightKeys.has(key)) return;

      const timer = setTimeout(() => {
        timerByKey.delete(key);
        inFlightKeys.add(key);
        void check(employerId, connectionIds, value, key);
      }, DEBOUNCE_MS);
      timerByKey.set(key, timer);
    });
  };

  return { validate };
};

export type PersonnelNumberValidationService = ReturnType<typeof createPersonnelNumberValidationService>;
